import { WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteRepositoryProjectMembership } from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse, Repository } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from './admin-required-override.ts'
import * as styles from './confirm-dialog.css.ts'

export type RemoveRepositoryDialogProps = {
  repository: Pick<Repository, 'id' | 'full_name' | 'type'>
  project: Pick<ProjectResponse, 'id' | 'name'>
  onClose: () => unknown
  onRemoved: () => unknown
}

/** Confirms taking a repository out of its project. Not destructive: the repository stays connected. */
export const RemoveRepositoryDialog: React.FC<RemoveRepositoryDialogProps> = ({
  repository,
  project,
  onClose,
  onRemoved,
}) => {
  const { addToast } = useToastService()
  const showError = useShowMutationError({
    prefix: 'Failed to remove repository',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const removeMutation = useDeleteRepositoryProjectMembership({
    onSuccess: () => {
      addToast({
        message: (
          <>
            {repository.full_name} removed from &ldquo;{project.name}&rdquo;
          </>
        ),
        variant: 'success',
      })
      onRemoved()
    },
    onError: showError,
  })
  const isPending = removeMutation.isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>
            <WarningIcon size={32} weight="fill" className={styles.cautionIcon} aria-hidden />
            Remove repository?
          </Dialog.Title>
          <div className={styles.subjectCard}>
            <span className={styles.subjectKind}>
              <SCMIcon type={repository.type} size="medium" />
              <span>Repository</span>
            </span>
            <p className={styles.subjectName}>{repository.full_name}</p>
          </div>
          <Dialog.Description asChild>
            <p className={styles.description}>
              It stays connected to Pixee but will no longer receive this project&apos;s feedback. You can move the
              repository back at any time.
            </p>
          </Dialog.Description>
          <div className={styles.buttons}>
            <GhostButton
              type="secondary"
              size="small"
              className={styles.outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="secondary"
              size="small"
              state={isPending ? 'loading' : 'default'}
              onClick={() => removeMutation.mutate({ repositoryId: repository.id })}
              loadingLabel="Removing repository"
            >
              Remove repository
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
